import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import * as EmailValidator from 'email-validator';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import AuthForm from './AuthForm';
import AuthField from './AuthField';
import MobileField from './MobileField';
import { AuthAPI } from '../../../api';

const DEFAULT_ERROR_MESSAGE = 'Error: check your details and try again.';

const METHOD_EMAIL = 'email';
const METHOD_MOBILE = 'mobile';

const COUNTRY_CODE = 'AU';

const parseMobile = (input) => parsePhoneNumberFromString(input || '', COUNTRY_CODE);

const isValidMobile = (input) => {
  const parsed = parseMobile(input);
  return !!parsed && parsed.isValid() && parsed.getType() === 'MOBILE';
};

const toE164 = (input) => {
  const parsed = parseMobile(input);
  return parsed ? parsed.format('E.164') : '';
};

const useStyles = makeStyles(theme => ({
  subtitle: {
    marginTop: '0.8rem',
    marginBottom: '2rem',
    color: theme.palette.text.secondary,
  },
  tabs: {
    display: 'flex',
    width: '100%',
    padding: '0.25rem',
    marginBottom: '1rem',
    borderRadius: '0.75rem',
    backgroundColor: theme.palette.common.paleGrey,
  },
  tab: {
    flex: 1,
    padding: '0.6rem 0',
    border: 'none',
    borderRadius: '0.6rem',
    background: 'none',
    cursor: 'pointer',
    fontSize: '0.95rem',
    color: theme.palette.text.secondary,
  },
  tabSelected: {
    backgroundColor: theme.palette.common.white,
    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
    fontWeight: 800,
    color: theme.palette.text.primary,
  },
  hint: {
    marginTop: '0.5rem',
    color: theme.palette.text.secondary,
  },
}));

const PasswordRecoveryForm = (props) => {
  const {
    onComplete,
    onMobileCodeSent,
    onBack,
    backLabel = 'Back to login',
    isProvider,
    title,
    initialMethod = METHOD_EMAIL,
  } = props;

  const classes = useStyles();

  const [method, setMethod] = useState(initialMethod);
  const [recoveryError, setRecoveryError] = useState();
  const [busy, setBusy] = useState(false);

  const [email, setEmail] = useState('');
  const [emailWarning, setEmailWarning] = useState();

  const [mobile, setMobile] = useState('');
  const [mobileWarning, setMobileWarning] = useState();

  const showMethodTabs = isProvider && !!onMobileCodeSent;
  const isMobile = showMethodTabs && method === METHOD_MOBILE;

  const validateEmail = (isSubmit = false) => {
    if (email === '') {
      setEmailWarning(isSubmit ? 'Email address is required' : undefined);
      return false;
    }
    if (!EmailValidator.validate(email)) {
      setEmailWarning('Enter a valid email address');
      return false;
    }
    setEmailWarning();
    return true;
  };

  const validateMobile = (isSubmit = false) => {
    if (mobile === '') {
      setMobileWarning(isSubmit ? 'Mobile number is required' : undefined);
      return false;
    }
    if (!isValidMobile(mobile)) {
      setMobileWarning('Enter a valid Australian mobile number');
      return false;
    }
    setMobileWarning();
    return true;
  };

  const selectMethod = (next) => {
    setMethod(next);
    setRecoveryError();
    setEmailWarning();
    setMobileWarning();
  };

  const recoverByEmail = async () => {
    if (!validateEmail(true)) return;
    setBusy(true);
    try {
      await AuthAPI.recoverPassword(email, isProvider);
      onComplete(email);
    } catch (err) {
      console.error(err);   // eslint-disable-line no-console
      const message = (err.body && err.body.message) ? err.body.message : DEFAULT_ERROR_MESSAGE;
      setRecoveryError(message);
    } finally {
      setBusy(false);
    }
  };

  const recoverByMobile = async () => {
    if (!validateMobile(true)) return;
    const phone = toE164(mobile);
    setBusy(true);
    try {
      await AuthAPI.recoverPasswordByPhone(phone, isProvider);
      onMobileCodeSent(phone);
    } catch (err) {
      console.error(err);   // eslint-disable-line no-console
      const message = (err.body && err.body.message) ? err.body.message : DEFAULT_ERROR_MESSAGE;
      setRecoveryError(message);
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = () => {
    setRecoveryError();
    return isMobile ? recoverByMobile() : recoverByEmail();
  };

  const renderTab = (value, label) => (
    <button
      type="button"
      className={`${classes.tab} ${method === value ? classes.tabSelected : ''}`}
      aria-pressed={method === value}
      onClick={() => selectMethod(value)}
    >
      {label}
    </button>
  );

  return (
    <AuthForm
      buttonLabel={isMobile ? 'Send Code' : 'Reset Password'}
      buttonAction={handleSubmit}
      disabled={isMobile ? mobileWarning !== undefined : emailWarning !== undefined}
      busy={busy}
      onCancel={onBack}
      cancelLabel={backLabel}
    >
      <Typography variant="h1" color="primary">
        {title}
      </Typography>

      {showMethodTabs && (
        <>
          <Typography variant="body1" className={classes.subtitle}>
            Choose how you&apos;d like to reset your password.
          </Typography>
          <div className={classes.tabs} role="group" aria-label="Recovery method">
            {renderTab(METHOD_EMAIL, 'Email')}
            {renderTab(METHOD_MOBILE, 'Mobile')}
          </div>
        </>
      )}

      {isMobile ? (
        <>
          <MobileField
            id="mobile"
            label="Mobile number"
            name="mobile"
            value={mobile}
            onChange={setMobile}
            onEnter={handleSubmit}
            onBlur={() => validateMobile()}
            error={mobileWarning || recoveryError}
          />
          <Typography variant="body2" className={classes.hint}>
            Must match the phone number on your account.
          </Typography>
        </>
      ) : (
        <>
          <AuthField
            id="email"
            label="Email Address"
            autoComplete="email"
            type="email"
            name="email"
            value={email}
            onChange={setEmail}
            onEnter={handleSubmit}
            error={emailWarning || recoveryError}
            onBlur={() => validateEmail()}
          />
          <Typography variant="body2" className={classes.hint}>
            Must match the email address on your account.
          </Typography>
        </>
      )}
    </AuthForm>
  );
};

PasswordRecoveryForm.propTypes = {
  onComplete: PropTypes.func.isRequired,
  onMobileCodeSent: PropTypes.func,
  onBack: PropTypes.func,
  backLabel: PropTypes.string,
  isProvider: PropTypes.bool.isRequired,
  title: PropTypes.string,
  initialMethod: PropTypes.oneOf([METHOD_EMAIL, METHOD_MOBILE]),
};

export default PasswordRecoveryForm;
